import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Check, X, Share2, Navigation, Phone, Camera, Loader } from "lucide-react";
import { api } from "@/lib/api";

const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);

function PhotoUpload({ jobId, phase, onDone, label }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      await api.post(`/applicator/jobs/${jobId}/photos`, { phase, urls });
      toast.success(`${label} uploaded (${urls.length})`);
      onDone();
    } catch (_) { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={pick} data-testid={`upload-${phase}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors disabled:opacity-60">
        {busy ? <Loader size={15} className="animate-spin" /> : <Camera size={15} />} {label}
      </button>
    </>
  );
}

function ReferModal({ jobId, onClose, onDone }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/applicator/jobs/${jobId}/refer-eligible`).then(({ data }) => setList(data)).catch(() => setList([])); }, [jobId]);
  const refer = async (aid) => {
    try { await api.post(`/applicator/jobs/${jobId}/refer`, { to_applicator_id: aid }); toast.success("Job referred"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not refer"); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-5" onClick={onClose} data-testid="refer-modal">
      <div className="hair-border bg-[#111113] p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><p className="errah-label gold-text">Refer to a certified applicator</p><button onClick={onClose}><X size={18} /></button></div>
        {!list ? <p className="text-gray-500 text-sm">Loading…</p> : list.length === 0 ? <p className="text-sm text-gray-500">No other eligible certified applicators found.</p> : (
          <div className="space-y-2">
            {list.map((a) => (
              <div key={a.applicator_id} className="flex items-center justify-between hair-border bg-white/[0.02] p-3">
                <div><p className="text-sm">{a.name} <span className="text-xs gold-text">{a.applicator_code}</span></p><p className="text-xs text-gray-500">{a.city} · {a.jobs_completed} jobs</p></div>
                <button onClick={() => refer(a.applicator_id)} data-testid={`refer-to-${a.applicator_code}`} className="text-xs bg-[#C9A45C] text-black px-3 py-1.5 font-semibold">Refer</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ApplicatorJobs() {
  const [tab, setTab] = useState("new");
  const [data, setData] = useState(null);
  const [referJob, setReferJob] = useState(null);

  const load = () => api.get("/applicator/jobs").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (jid, action, body) => {
    try { await api.post(`/applicator/jobs/${jid}/${action}`, body || {}); toast.success(action + " ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || `Could not ${action}`); }
  };

  if (!data) return <div className="text-gray-500">Loading…</div>;
  const tabs = [["new", "New", data.new], ["active", "Active", data.active], ["completed", "Completed", data.completed], ["referred", "Referred", data.referred]];
  const jobs = { new: data.new, active: data.active, completed: data.completed, referred: data.referred }[tab];

  return (
    <div data-testid="applicator-jobs-page">
      <h1 className="serif-head text-3xl lg:text-4xl mb-5">Jobs</h1>
      <div className="flex gap-2 mb-6" data-testid="jobs-tabs">
        {tabs.map(([k, label, arr]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`jobs-tab-${k}`} className={`px-4 py-2 text-xs border ${tab === k ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/15 text-gray-400"}`}>{label} ({arr.length})</button>
        ))}
      </div>

      <div className="space-y-3">
        {jobs.length === 0 && <p className="text-sm text-gray-600">No jobs here.</p>}
        {jobs.map((j) => (
          <div key={j.id} className="hair-border bg-white/[0.02] p-5" data-testid={`job-card-${j.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg">{j.service}</p>
                <p className="text-sm text-gray-400">{j.customer_name || "Customer"} · {j.city || "—"} {j.pincode ? `· ${j.pincode}` : ""}</p>
                <p className="text-xs text-gray-500 mt-1">Job {j.id.slice(0, 12)} · {j.preferred_date || "flexible"} · Value {money(j.estimated_value)}</p>
              </div>
              <span className="text-[10px] tracking-widest px-2 py-1 border border-[#C9A45C]/40 text-[#E4C987] uppercase">{j.status}{j.qc_status ? ` · ${j.qc_status}` : ""}</span>
            </div>

            {tab === "new" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => act(j.id, "accept")} data-testid={`accept-${j.id}`} className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold"><Check size={15} /> Accept</button>
                <button onClick={() => act(j.id, "decline")} data-testid={`decline-${j.id}`} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors"><X size={15} /> Decline</button>
                <button onClick={() => setReferJob(j.id)} data-testid={`refer-${j.id}`} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors"><Share2 size={15} /> Refer</button>
              </div>
            )}

            {tab === "active" && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <a href={`https://maps.google.com/?q=${encodeURIComponent((j.city || "") + " " + (j.pincode || ""))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors"><Navigation size={15} /> Navigate</a>
                  {j.status === "assigned" && <StartBlock job={j} onDone={load} />}
                  {j.status === "in_progress" && (
                    <>
                      <PhotoUpload jobId={j.id} phase="during" label="During Photos" onDone={load} />
                      <PhotoUpload jobId={j.id} phase="after" label="After Photos" onDone={load} />
                      <button onClick={() => act(j.id, "complete", { after_photos: j.after_photos || [] })} data-testid={`complete-${j.id}`} className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold"><Check size={15} /> Complete Job</button>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500">Before: {(j.before_photos || []).length} · During: {(j.during_photos || []).length} · After: {(j.after_photos || []).length}</p>
              </div>
            )}

            {tab === "completed" && (
              <p className="mt-3 text-xs text-gray-500">Completed {j.completed_at?.slice(0, 10)} · QC {j.qc_status} · Customer {j.customer_confirmed ? "confirmed ✓" : "pending"} · Rating {j.customer_rating || "—"}</p>
            )}
            {tab === "referred" && <p className="mt-3 text-xs text-gray-500">Referred to another applicator · status {j.status}</p>}
          </div>
        ))}
      </div>

      {referJob && <ReferModal jobId={referJob} onClose={() => setReferJob(null)} onDone={() => { setReferJob(null); load(); }} />}
    </div>
  );
}

function StartBlock({ job, onDone }) {
  const [photos, setPhotos] = useState([]);
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); urls.push(data.url); }
      setPhotos((p) => [...p, ...urls]); toast.success(`${urls.length} before photo(s) added`);
    } catch (_) { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const start = async () => {
    if (!photos.length) { toast.error("Add before photos first"); return; }
    try { await api.post(`/applicator/jobs/${job.id}/start`, { before_photos: photos }); toast.success("Job started"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={pick} data-testid={`before-upload-${job.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors disabled:opacity-60"><Camera size={15} /> Before Photos ({photos.length})</button>
      <button onClick={start} data-testid={`start-${job.id}`} className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold">Start Job</button>
    </>
  );
}
